import React, { useState, useEffect } from 'react';
import { apiAdmin, setSuperAdminToken, getDisplayMessage } from '@/api/apiClient';

const STORAGE_OPTIONS = [
  { value: 'fs', label: 'Disco local (UPLOAD_DIR)' },
  { value: 's3', label: 'MinIO / S3' },
  { value: 'gdrive', label: 'Google Drive' },
];

/** Remove ?hl=… e extrai ID se colarem a URL completa. */
function sanitizeParentFolderIdInput(raw) {
  let t = (raw ?? '').trim();
  if (!t) return '';
  const m = t.match(/\/folders\/([a-zA-Z0-9_-]+)/);
  if (m?.[1]) return m[1];
  const q = t.indexOf('?');
  if (q >= 0) t = t.slice(0, q);
  const hash = t.indexOf('#');
  if (hash >= 0) t = t.slice(0, hash);
  return t.trim().replace(/[^a-zA-Z0-9_-]/g, '');
}

export default function AdminConfigGDrive() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [erro, setErro] = useState('');
  const [success, setSuccess] = useState('');
  const [testMsg, setTestMsg] = useState('');
  const [testErr, setTestErr] = useState('');
  const [testHint, setTestHint] = useState('');
  const [form, setForm] = useState({
    storageMode: 'fs',
    parentFolderId: '',
    serviceAccountJson: '',
  });

  const carregar = async () => {
    setLoading(true);
    setErro('');
    try {
      const data = await apiAdmin.get('/api/admin/config/gdrive');
      setConfig(data);
      setForm({
        storageMode: data.storageMode || 'fs',
        parentFolderId: sanitizeParentFolderIdInput(data.parentFolderId || ''),
        serviceAccountJson: data.serviceAccountJsonPlaceholder === '********' ? '********' : '',
      });
    } catch (err) {
      if (err?.status === 401) {
        setSuperAdminToken(null);
        window.location.href = '/admin';
        return;
      }
      setErro(getDisplayMessage(err, 'Erro ao carregar configuração. Tente novamente.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErro('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErro('');
    setSuccess('');
    try {
      const parentFolderId = sanitizeParentFolderIdInput(form.parentFolderId);
      const payload = {
        storageMode: form.storageMode,
        parentFolderId: parentFolderId || undefined,
      };
      if (form.serviceAccountJson && form.serviceAccountJson !== '********') {
        payload.serviceAccountJson = form.serviceAccountJson.trim();
      }
      const data = await apiAdmin.put('/api/admin/config/gdrive', payload);
      setConfig(data);
      setForm((prev) => ({
        ...prev,
        storageMode: data.storageMode || prev.storageMode,
        parentFolderId: data.parentFolderId || '',
        serviceAccountJson: data.serviceAccountConfigured ? '********' : '',
      }));
      setSuccess('Configuração de armazenamento Certify salva com sucesso.');
    } catch (err) {
      setErro(getDisplayMessage(err, 'Erro ao salvar configuração. Tente novamente.'));
    } finally {
      setSaving(false);
    }
  };

  const testarConexao = async () => {
    setTesting(true);
    setTestErr('');
    setTestMsg('');
    setTestHint('');
    try {
      const data = await apiAdmin.post('/api/admin/config/gdrive/test', {});
      if (data?.ok) {
        setTestMsg(data.message || 'Conexão com Google Drive OK.');
      } else {
        setTestErr(data?.error || 'Falha no teste.');
        if (typeof data?.hint === 'string') setTestHint(data.hint);
      }
    } catch (err) {
      const body = err?.data;
      setTestErr(
        body && typeof body.error === 'string'
          ? body.error
          : getDisplayMessage(err, 'Falha ao testar conexão.')
      );
      if (body && typeof body.hint === 'string') setTestHint(body.hint);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[200px]">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-700 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <h1 className="text-2xl font-semibold text-slate-800 mb-2">AuctionCertify — Google Drive</h1>
      <p className="text-sm text-slate-600 mb-4">
        Credenciais e modo de armazenamento são guardados na base de dados (<code className="text-xs">system_config</code>
        ) e sobrevivem a redeploys do contentor. Os ficheiros selados ficam no Google Drive; downloads passam sempre pela API.
      </p>
      <p className="text-sm mb-6">
        <a href="/admin/config/certify-gdrive-link" className="text-primary font-medium underline">
          Certify ↔ Drive — ligar evidência existente a um ficheiro na pasta
        </a>
      </p>

      {erro && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-800 text-sm border border-red-200">{erro}</div>
      )}
      {success && (
        <div className="mb-4 p-3 rounded-lg bg-green-50 text-green-800 text-sm border border-green-200">
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6 bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div>
          <label htmlFor="storageMode" className="block text-sm font-medium text-slate-700 mb-1">
            Modo de armazenamento (novos uploads Certify)
          </label>
          <select
            id="storageMode"
            name="storageMode"
            value={form.storageMode}
            onChange={handleChange}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            {STORAGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="parentFolderId" className="block text-sm font-medium text-slate-700 mb-1">
            ID da pasta raiz no Google Drive
          </label>
          <input
            id="parentFolderId"
            name="parentFolderId"
            type="text"
            value={form.parentFolderId}
            onChange={handleChange}
            placeholder="Ex.: 1AbCdEfGhIjKlMnOpQrStUvWxYz"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
          />
          <p className="text-xs text-slate-500 mt-1">
            Use só o ID, por exemplo <span className="font-mono">1sHsZTC5LwIQwWfBm2xNSDJaQ71ASNMRx</span> —{' '}
            <strong>não</strong> inclua <span className="font-mono">?hl=pt-br</span> nem a URL completa. Partilhe a
            pasta com o e-mail da service account (Editor).
          </p>
        </div>

        <div>
          <label htmlFor="serviceAccountJson" className="block text-sm font-medium text-slate-700 mb-1">
            JSON da Service Account (Google Cloud)
          </label>
          <textarea
            id="serviceAccountJson"
            name="serviceAccountJson"
            value={form.serviceAccountJson}
            onChange={handleChange}
            rows={8}
            placeholder='Cole o conteúdo do ficheiro JSON da service account…'
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-xs font-mono"
          />
          {config?.serviceAccountConfigured && config?.serviceAccountEmail && (
            <p className="text-xs text-slate-600 mt-1">
              Conta configurada: <span className="font-mono">{config.serviceAccountEmail}</span>
            </p>
          )}
          <p className="text-xs text-slate-500 mt-1">
            Deixe <strong>********</strong> para manter o JSON já guardado. Ative a API Google Drive no projeto GCP.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-slate-800 text-white text-sm font-medium hover:bg-slate-700 disabled:opacity-50"
          >
            {saving ? 'A guardar…' : 'Guardar'}
          </button>
          <button
            type="button"
            onClick={testarConexao}
            disabled={testing || form.storageMode !== 'gdrive'}
            className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
          >
            {testing ? 'A testar…' : 'Testar conexão'}
          </button>
        </div>
      </form>

      {testMsg && (
        <p className="mt-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">{testMsg}</p>
      )}
      {testErr && (
        <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 space-y-2">
          <p>{testErr}</p>
          {testHint && <p className="text-red-800/90">{testHint}</p>}
        </div>
      )}

      <div className="mt-8 text-xs text-slate-500 space-y-2">
        <p>
          <strong>Passos no Google Cloud:</strong> criar projeto → ativar API Drive → criar Service Account → gerar chave JSON
          → criar pasta no Drive.
        </p>
        <p>
          <strong>Partilha obrigatória:</strong> no Google Drive, botão direito na pasta → Partilhar → adicionir o e-mail da
          service account (ex.: <span className="font-mono">…@….iam.gserviceaccount.com</span>) como <strong>Editor</strong>.
          Sem isto a API devolve &quot;File not found&quot;.
        </p>
        <p>
          Registos antigos em disco ou S3 continuam acessíveis; apenas novos uploads Certify usam o modo seleccionado.
        </p>
      </div>
    </div>
  );
}
